import { useState, useEffect } from 'react';
import { UserPlus, Search, Mail, Phone, Calendar, Trash2, X, ExternalLink, MessageCircle, Clock, CheckCircle, Eye, Star, Users, Folder } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import CustomListbox from '../components/CustomListbox';
import InfluencerAvatar from '../components/InfluencerAvatar';
import { RecruitmentInfluencer, Collab, getDocuments, updateDocument, deleteDocument } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';

const STATUSES = [
  { value: 'spotted', label: 'Repéré', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'contacted', label: 'Contacté', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'validated', label: 'Validé', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'in_agency', label: 'En agence', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'refused', label: 'Refusé', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
] as const;

const COMMON_TAGS = [
  'beauté', 'sport', 'premium', 'lifestyle', 'mode', 'tech', 'gaming', 
  'food', 'travel', 'fitness', 'micro-influenceur', 'macro-influenceur',
  'nano-influenceur', 'célébrité', 'famille', 'parentalité', 'diy',
  'art', 'musique', 'dance', 'comedy', 'education'
];

export default function Recruitment() {
  const { currentUser, userProfile } = useAuth();
  const [influencers, setInfluencers] = useState<RecruitmentInfluencer[]>([]);
  const [campaigns, setCampaigns] = useState<Collab[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<RecruitmentInfluencer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [memberFilter, setMemberFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'all' | 'by-member'>('by-member');
  const [currentTag, setCurrentTag] = useState('');
  const [formData, setFormData] = useState<Partial<any>>({
    full_name: '',
    username: '',
    email: '',
    phone: '',
    instagram_url: '',
    tiktok_url: '',
    youtube_url: '',
    profile_picture: '',
    tags: [],
    collaborated_brands: '',
    status: 'spotted',
    last_contact_date: '',
    next_follow_up_date: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const { supabase } = await import('../lib/supabase');

      const [influencersResult, campaignsData, usersResult] = await Promise.all([
        supabase.from('recruitment_influencers').select('*'),
        getDocuments<Collab>('collabs'),
        supabase.from('users').select('id, first_name, last_name, email')
      ]);

      console.log('Influencers result:', influencersResult);
      console.log('Users result:', usersResult);

      if (influencersResult.error) {
        console.error('Error loading influencers:', influencersResult.error);
        setInfluencers([]);
      } else {
        const usersMap = new Map((usersResult.data || []).map((u: any) => [u.id, u]));

        const influencersWithCreator = (influencersResult.data || []).map((inf: any) => {
          const creator = usersMap.get(inf.created_by);
          const creatorName = creator
            ? `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.email
            : 'Utilisateur inconnu';
          return {
            ...inf,
            created_by_name: creatorName
          };
        });

        console.log('Influencers with creator:', influencersWithCreator);
        setInfluencers(influencersWithCreator);
      }

      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setInfluencers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const influencerData: any = {
        full_name: formData.full_name,
        username: formData.username,
        email: formData.email || null,
        phone: formData.phone || null,
        instagram_url: formData.instagram_url || null,
        tiktok_url: formData.tiktok_url || null,
        youtube_url: formData.youtube_url || null,
        profile_picture: formData.profile_picture || null,
        tags: formData.tags || [],
        collaborated_brands: formData.collaborated_brands || null,
        status: formData.status,
        last_contact_date: formData.last_contact_date ? (formData.last_contact_date.includes('T') ? formData.last_contact_date : new Date(formData.last_contact_date + 'T00:00:00').toISOString()) : null,
        next_follow_up_date: formData.next_follow_up_date ? (formData.next_follow_up_date.includes('T') ? formData.next_follow_up_date : new Date(formData.next_follow_up_date + 'T00:00:00').toISOString()) : null,
        contact_notes: formData.notes || null,
      };

      if (selectedInfluencer?.id) {
        console.log('Updating influencer:', selectedInfluencer.id, influencerData);
        await updateDocument('recruitment_influencers', selectedInfluencer.id, influencerData);
        console.log('Update successful');
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving influencer:', error);
      alert('Erreur lors de la sauvegarde. Vérifiez la console pour plus de détails.');
    }
  };

  const handleDelete = async (influencerId: string) => {
    try {
      await deleteDocument('recruitment_influencers', influencerId);
      await loadData();
    } catch (error) {
      console.error('Error deleting influencer:', error);
    }
  };

  const handleCardClick = (influencer: RecruitmentInfluencer) => {
    setSelectedInfluencer(influencer);
    setFormData({
      full_name: influencer.full_name || (influencer as any).fullName,
      username: influencer.username,
      email: (influencer as any).email || '',
      phone: (influencer as any).phone || '',
      instagram_url: influencer.instagram_url || (influencer as any).instagramUrl || '',
      tiktok_url: influencer.tiktok_url || (influencer as any).tiktokUrl || '',
      youtube_url: influencer.youtube_url || (influencer as any).youtubeUrl || '',
      profile_picture: (influencer as any).profile_picture || '',
      tags: influencer.tags || [],
      collaborated_brands: influencer.collaborated_brands || (influencer as any).collaboratedBrands || '',
      status: influencer.status,
      last_contact_date: (influencer as any).last_contact_date ? new Date((influencer as any).last_contact_date).toISOString().split('T')[0] : '',
      next_follow_up_date: (influencer as any).next_follow_up_date ? new Date((influencer as any).next_follow_up_date).toISOString().split('T')[0] : '',
      notes: (influencer as any).contact_notes || '',
    });
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedInfluencer(null);
    setFormData({
      full_name: '',
      username: '',
      email: '',
      phone: '',
      instagram_url: '',
      tiktok_url: '',
      youtube_url: '',
      profile_picture: '',
      tags: [],
      collaborated_brands: '',
      status: 'spotted',
      last_contact_date: '',
      next_follow_up_date: '',
      notes: '',
    });
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags?.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const renderStars = (rating: number, interactive = false, onChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive && onChange ? () => onChange(star) : undefined}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`w-4 h-4 ${
                star <= rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-400'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = STATUSES.find(s => s.value === status);
    return statusConfig || STATUSES[0];
  };

  const filteredInfluencers = influencers.filter(influencer => {
    const fullName = influencer.full_name || (influencer as any).fullName || '';
    const username = influencer.username || '';
    const tags = influencer.tags || [];
    const createdBy = (influencer as any).created_by;

    const matchesSearch =
      fullName.toLowerCase().includes(search.toLowerCase()) ||
      username.toLowerCase().includes(search.toLowerCase()) ||
      tags.some((tag: string) => tag.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || influencer.status === statusFilter;
    const matchesTag = tagFilter === 'all' || tags.includes(tagFilter);
    const matchesMember = memberFilter === 'all' || createdBy === memberFilter;

    return matchesSearch && matchesStatus && matchesTag && matchesMember;
  });

  const allTags = [...new Set(influencers.flatMap(inf => inf.tags))].sort();

  const allMembers = Array.from(
    new Map(
      influencers
        .filter(inf => (inf as any).created_by && (inf as any).created_by_name)
        .map(inf => [(inf as any).created_by, { id: (inf as any).created_by, name: (inf as any).created_by_name }])
    ).values()
  );

  const influencersByMember = allMembers.map(member => ({
    ...member,
    influencers: filteredInfluencers.filter(inf => (inf as any).created_by === member.id)
  })).filter(group => group.influencers.length > 0);

  const statusCounts = {
    spotted: influencers.filter(inf => inf.status === 'spotted').length,
    contacted: influencers.filter(inf => inf.status === 'contacted').length,
    validated: influencers.filter(inf => inf.status === 'validated').length,
    in_agency: influencers.filter(inf => inf.status === 'in_agency').length,
    refused: influencers.filter(inf => inf.status === 'refused').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
          <UserPlus className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Recrutement</h1>
          <p className="text-white/50">Suivez et gérez vos influenceurs en cours de recrutement</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white/[0.04] rounded-lg border border-blue-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-400">Repérés</p>
              <p className="text-2xl font-bold text-white mt-1">{statusCounts.spotted}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Eye className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>
        <div className="bg-white/[0.04] rounded-lg border border-amber-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-400">Contactés</p>
              <p className="text-2xl font-bold text-white mt-1">{statusCounts.contacted}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </div>
        <div className="bg-white/[0.04] rounded-lg border border-green-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-400">Validés</p>
              <p className="text-2xl font-bold text-white mt-1">{statusCounts.validated}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>
        <div className="bg-white/[0.04] rounded-lg border border-purple-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-400">En agence</p>
              <p className="text-2xl font-bold text-white mt-1">{statusCounts.in_agency}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>
        <div className="bg-white/[0.04] rounded-lg border border-red-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-400">Refusés</p>
              <p className="text-2xl font-bold text-white mt-1">{statusCounts.refused}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6">
        {/* View mode toggle */}
        <div className="flex items-center gap-2 mb-6 pb-6 border-b border-white/[0.06]">
          <button
            onClick={() => setViewMode('by-member')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'by-member'
                ? 'bg-white/[0.08] text-white border-2 border-white/20'
                : 'bg-white/[0.04] text-white/60 border-2 border-transparent hover:bg-white/[0.08]'
            }`}
          >
            <Folder className="w-4 h-4" />
            Par membre
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'all'
                ? 'bg-white/[0.08] text-white border-2 border-white/20'
                : 'bg-white/[0.04] text-white/60 border-2 border-transparent hover:bg-white/[0.08]'
            }`}
          >
            <Users className="w-4 h-4" />
            Tous
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Rechercher par nom, pseudo ou tag..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
              />
            </div>
          </div>
          <CustomListbox
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'Tous les statuts' },
              ...STATUSES.map(s => ({ value: s.value, label: s.label }))
            ]}
            className="w-full lg:w-48"
          />
          <CustomListbox
            value={tagFilter}
            onChange={setTagFilter}
            options={[
              { value: 'all', label: 'Tous les tags' },
              ...allTags.map(t => ({ value: t, label: t }))
            ]}
            className="w-full lg:w-48"
          />
          {viewMode === 'all' && allMembers.length > 0 && (
            <CustomListbox
              value={memberFilter}
              onChange={setMemberFilter}
              options={[
                { value: 'all', label: 'Tous les membres' },
                ...allMembers.map(m => ({ value: m.id, label: m.name }))
              ]}
              className="w-full lg:w-48"
            />
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60"></div>
          </div>
        ) : viewMode === 'by-member' ? (
          <div className="space-y-8">
            {influencersByMember.map((group) => (
              <div key={group.id} className="space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b-2 border-white/[0.06]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md">
                    {group.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{group.name}</h3>
                    <p className="text-sm text-white/50">{group.influencers.length} influenceur{group.influencers.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.influencers.map((influencer) => {
                    const fullName = influencer.full_name || (influencer as any).fullName || 'Nom inconnu';
                    const email = (influencer as any).email;
                    const phone = (influencer as any).phone;
                    const nextFollowUp = (influencer as any).next_follow_up_date;
                    const responseReceived = (influencer as any).response_received;
                    const profilePictureUrl = (influencer as any).profile_picture;

                    return (
                    <div
                      key={influencer.id}
                      onClick={() => handleCardClick(influencer)}
                      className="bg-white/[0.04] rounded-xl p-5 hover:bg-white/[0.06] transition-all duration-200 border border-white/[0.08] hover:border-white/20 cursor-pointer group relative"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(influencer.id!);
                        }}
                        className="absolute top-3 right-3 p-2 text-white/30 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <InfluencerAvatar
                            profileImageUrl={profilePictureUrl}
                            name={fullName}
                            size="md"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-bold truncate text-lg">{fullName}</h3>
                            <p className="text-white/50 text-sm">@{influencer.username}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${
                            influencer.status === 'spotted' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                            influencer.status === 'contacted' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                            influencer.status === 'validated' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                            'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {getStatusBadge(influencer.status).label}
                          </span>
                          {responseReceived && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                              <CheckCircle className="w-3 h-3" />
                              Répondu
                            </span>
                          )}
                        </div>

                        {(email || phone) && (
                          <div className="space-y-1.5 pt-2 border-t border-white/[0.06]">
                            {email && (
                              <div className="flex items-center gap-2 text-sm text-white/50">
                                <Mail className="w-4 h-4 text-white/30 flex-shrink-0" />
                                <span className="truncate">{email}</span>
                              </div>
                            )}
                            {phone && (
                              <div className="flex items-center gap-2 text-sm text-white/50">
                                <Phone className="w-4 h-4 text-white/30 flex-shrink-0" />
                                <span>{phone}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {nextFollowUp && (
                          <div className="flex items-center gap-2 text-xs pt-2">
                            <Clock className="w-3.5 h-3.5 text-white/30" />
                            <span className={`${
                              new Date(nextFollowUp) < new Date()
                                ? 'text-red-400 font-semibold'
                                : 'text-white/50'
                            }`}>
                              Suivi: {new Date(nextFollowUp).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            ))}
            {influencersByMember.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-white/30" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Aucun influenceur trouvé</h3>
                <p className="text-white/50">Ajoutez des influenceurs depuis la page Discover</p>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInfluencers.map((influencer) => {
              const fullName = influencer.full_name || (influencer as any).fullName || 'Nom inconnu';
              const email = (influencer as any).email;
              const phone = (influencer as any).phone;
              const nextFollowUp = (influencer as any).next_follow_up_date;
              const responseReceived = (influencer as any).response_received;
              const profilePictureUrl = (influencer as any).profile_picture;
              const createdByName = (influencer as any).created_by_name;

              return (
              <div
                key={influencer.id}
                onClick={() => handleCardClick(influencer)}
                className="bg-white/[0.04] rounded-xl p-5 hover:bg-white/[0.06] transition-all duration-200 border border-white/[0.08] hover:border-white/20 cursor-pointer group relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(influencer.id!);
                  }}
                  className="absolute top-3 right-3 p-2 text-white/30 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <InfluencerAvatar
                      profileImageUrl={profilePictureUrl}
                      name={fullName}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold truncate text-lg">{fullName}</h3>
                      <p className="text-white/50 text-sm">@{influencer.username}</p>
                      {createdByName && (
                        <p className="text-xs text-white/40 mt-0.5 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {createdByName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${
                      influencer.status === 'spotted' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      influencer.status === 'contacted' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                      influencer.status === 'validated' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {getStatusBadge(influencer.status).label}
                    </span>
                    {responseReceived && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                        <CheckCircle className="w-3 h-3" />
                        Répondu
                      </span>
                    )}
                  </div>

                  {(email || phone) && (
                    <div className="space-y-1.5 pt-2 border-t border-white/[0.06]">
                      {email && (
                        <div className="flex items-center gap-2 text-sm text-white/50">
                          <Mail className="w-4 h-4 text-white/30 flex-shrink-0" />
                          <span className="truncate">{email}</span>
                        </div>
                      )}
                      {phone && (
                        <div className="flex items-center gap-2 text-sm text-white/50">
                          <Phone className="w-4 h-4 text-white/30 flex-shrink-0" />
                          <span>{phone}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {nextFollowUp && (
                    <div className="flex items-center gap-2 text-xs pt-2">
                      <Clock className="w-3.5 h-3.5 text-white/30" />
                      <span className={`${
                        new Date(nextFollowUp) < new Date()
                          ? 'text-red-400 font-semibold'
                          : 'text-white/50'
                      }`}>
                        Suivi: {new Date(nextFollowUp).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
            })}

            {filteredInfluencers.length === 0 && (
              <div className="col-span-full text-center py-16">
                <div className="w-16 h-16 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-white/30" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Aucun influenceur trouvé</h3>
                <p className="text-white/50">Ajoutez des influenceurs depuis la page Discover</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modale d'édition */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] rounded-2xl max-w-3xl w-full mx-4 p-8 border border-white/[0.08]">
            <button
              onClick={handleCloseModal}
              className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <Dialog.Title className="text-2xl font-bold text-white mb-6">
              {selectedInfluencer?.full_name || selectedInfluencer?.username}
            </Dialog.Title>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations de contact */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Informations de contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                </div>
              </div>

              {/* Dates de suivi */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Suivi</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Dernier contact
                    </label>
                    <input
                      type="date"
                      value={formData.last_contact_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_contact_date: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Prochain suivi
                    </label>
                    <input
                      type="date"
                      value={formData.next_follow_up_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    />
                  </div>
                </div>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  Statut
                </label>
                <CustomListbox
                  value={formData.status}
                  onChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  options={STATUSES.map(s => ({ value: s.value, label: s.label }))}
                />
              </div>

              {/* Marques collaborées */}
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Marques collaborées
                </label>
                <input
                  type="text"
                  value={formData.collaborated_brands}
                  onChange={(e) => setFormData(prev => ({ ...prev, collaborated_brands: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                  placeholder="Ex: Nike, Adidas, L'Oréal..."
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-white/[0.08] text-white/70"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-white"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    placeholder="Ajouter un tag..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors font-medium"
                  >
                    Ajouter
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {COMMON_TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        if (!formData.tags?.includes(tag)) {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...(prev.tags || []), tag]
                          }));
                        }
                      }}
                      className="px-2.5 py-1 text-xs font-medium text-white/50 bg-white/[0.06] rounded-full hover:bg-white/[0.10] transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Notes internes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 resize-none"
                  placeholder="Ajoutez des notes sur cet influenceur..."
                />
              </div>

              {/* Boutons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2.5 text-sm font-medium bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 text-sm font-semibold text-black bg-white hover:bg-white/90 rounded-xl transition-colors"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
